function getDefaultWindow() {
  return typeof window === 'undefined' ? null : window
}

function getDefaultRoot(targetWindow) {
  return targetWindow?.document?.documentElement || null
}

function getAnimationFrame(targetWindow) {
  const request = targetWindow?.requestAnimationFrame
  if (typeof request === 'function') {
    return (callback) => request.call(targetWindow, callback)
  }
  return (callback) => setTimeout(callback, 0)
}

function getCancelAnimationFrame(targetWindow) {
  const cancel = targetWindow?.cancelAnimationFrame
  if (typeof cancel === 'function') {
    return (id) => cancel.call(targetWindow, id)
  }
  return (id) => clearTimeout(id)
}

function isEditableElement(element) {
  const tagName = String(element?.tagName || '').toLowerCase()
  return tagName === 'input' || tagName === 'textarea' || tagName === 'select' || element?.isContentEditable
}

function isInsideLoginShell(element) {
  return typeof element?.closest === 'function' && Boolean(element.closest('.login-shell'))
}

function isStandaloneApp(targetWindow) {
  const standaloneMedia = typeof targetWindow?.matchMedia === 'function'
    ? targetWindow.matchMedia('(display-mode: standalone)')?.matches
    : false
  const standaloneNavigator = targetWindow?.navigator?.standalone === true
  return Boolean(standaloneMedia || standaloneNavigator)
}

export function readAppViewportHeight(targetWindow = getDefaultWindow()) {
  const visualViewportHeight = Number(targetWindow?.visualViewport?.height || 0)
  const innerHeight = Number(targetWindow?.innerHeight || 0)

  if (
    isStandaloneApp(targetWindow) &&
    Number.isFinite(innerHeight) &&
    innerHeight > 0 &&
    !isEditableElement(targetWindow?.document?.activeElement)
  ) {
    return innerHeight
  }

  if (Number.isFinite(visualViewportHeight) && visualViewportHeight > 0) {
    return visualViewportHeight
  }

  if (Number.isFinite(innerHeight) && innerHeight > 0) {
    return innerHeight
  }

  return 0
}

export function writeAppViewportHeight({
  root = getDefaultRoot(getDefaultWindow()),
  targetWindow = getDefaultWindow()
} = {}) {
  const viewportHeight = readAppViewportHeight(targetWindow)
  if (!root || !viewportHeight) return
  const viewportOffsetTop = Number(targetWindow?.visualViewport?.offsetTop || 0)
  root.style.setProperty('--app-viewport-height', `${viewportHeight}px`)
  root.style.setProperty('--app-viewport-offset-top', `${Number.isFinite(viewportOffsetTop) ? viewportOffsetTop : 0}px`)
}

export function installAppViewportHeightSync({
  root = getDefaultRoot(getDefaultWindow()),
  targetWindow = getDefaultWindow()
} = {}) {
  if (!root || !targetWindow) return () => {}

  const requestFrame = getAnimationFrame(targetWindow)
  const cancelFrame = getCancelAnimationFrame(targetWindow)
  const visualViewport = targetWindow.visualViewport
  let frameId = 0
  let focusFrameId = 0

  const scrollFocusedFieldIntoView = () => {
    if (focusFrameId) {
      cancelFrame(focusFrameId)
    }

    focusFrameId = requestFrame(() => {
      focusFrameId = 0
      const activeElement = targetWindow.document?.activeElement
      if (!isEditableElement(activeElement) || typeof activeElement.scrollIntoView !== 'function') return
      if (isInsideLoginShell(activeElement)) return
      activeElement.scrollIntoView({ block: 'center', inline: 'nearest' })
    })
  }

  const sync = () => {
    if (frameId) {
      cancelFrame(frameId)
    }

    frameId = requestFrame(() => {
      frameId = 0
      writeAppViewportHeight({ root, targetWindow })
    })
  }

  const syncForKeyboard = () => {
    sync()
    scrollFocusedFieldIntoView()
  }

  writeAppViewportHeight({ root, targetWindow })
  targetWindow.addEventListener?.('resize', sync)
  targetWindow.addEventListener?.('orientationchange', sync)
  targetWindow.addEventListener?.('focusin', syncForKeyboard)
  visualViewport?.addEventListener?.('resize', syncForKeyboard)
  visualViewport?.addEventListener?.('scroll', syncForKeyboard)

  return () => {
    if (frameId) {
      cancelFrame(frameId)
      frameId = 0
    }
    if (focusFrameId) {
      cancelFrame(focusFrameId)
      focusFrameId = 0
    }

    targetWindow.removeEventListener?.('resize', sync)
    targetWindow.removeEventListener?.('orientationchange', sync)
    targetWindow.removeEventListener?.('focusin', syncForKeyboard)
    visualViewport?.removeEventListener?.('resize', syncForKeyboard)
    visualViewport?.removeEventListener?.('scroll', syncForKeyboard)
  }
}
